<template lang="pug">
component(
    data-element-type="universalLink"
    :is="displayComponent.is"
    v-bind="displayComponent.attributes"
    v-on="displayComponent.events"
    @click="trackLinkEvent"
    @click.native="trackLinkEvent"
)
    slot
</template>

<script>
// FlexLink + WidgetLink's ugly stepchild
import getRefittedUrlMixin from '@/helpers/getRefittedUrlMixin'
import AffirmRatesWrapper from '@/integrations/affirm/affirmRatesWrapper'

export default {
    name: 'UniversalLink',
    mixins: [getRefittedUrlMixin],
    props: {
        link: {
            type: String,
            default: null,
        },
        element: {
            type: String,
            default: 'div',
        },
        hasColorOverride: {
            type: Boolean,
            default: false,
        },
        trackingEventType: {
            type: String,
            default: null,
        },
        openNewWindow: {
            type: Boolean,
            default: null,
        },
    },
    computed: {
        displayComponent() {
            const rootDisplayObj = {
                is: this.element,
                events: {},
                attributes: {},
                trackingData: {},
            }

            if (!this.link) return rootDisplayObj

            rootDisplayObj.attributes.class = ['cursor-pointer']
            // If a color is not being specified for the link, take on the color of the parent.
            if (!this.hasColorOverride) rootDisplayObj.attributes.class.push('text-inherit')

            // if the link type needs args then we have to split them here.
            // currently HIDE/SHOW are the only ones with args which is why this only looks for #
            const [linkType, linkArgs] = this.link.split('#')

            switch (linkType) {
                case 'AFFIRM':
                    // We only want to do this if
                    //   - affirm is enabled
                    //   - we are not in preview mode
                    // aka this component should only work when the page is live and affirm is enabled on the site
                    if (this.$affirm.enabled && !this.$storyblok.isEditing)
                        rootDisplayObj.is = AffirmRatesWrapper
                    break

                case 'CHAT':
                    rootDisplayObj.events.click = this.openChat
                    break

                case 'FITMENT':
                    rootDisplayObj.events.click = this.openFitment
                    break

                case 'ORDER':
                    rootDisplayObj.events.click = this.openOrderLogin
                    break

                case 'ACCOUNT':
                    rootDisplayObj.events.click = this.openAccountLogin
                    break

                case 'ISSD':
                    rootDisplayObj.events.click = this.openISSD
                    break

                case 'PHONE':
                    rootDisplayObj.is = 'a'
                    rootDisplayObj.attributes.href = `tel:${this.$sitewideData.config.phoneGeneral}`
                    break

                case 'HIDE':
                    rootDisplayObj.events.click = () => this.hideSelector(linkArgs)
                    break

                case 'SHOW':
                    rootDisplayObj.events.click = () => this.showSelector(linkArgs)
                    break

                default:
                    // if they want to open a url in a new window, we do NO massaging (for now)
                    // TODO: be smarter about url generation to cover off on possible gaps (what if they want a cat page to open in a new tab -- likely will nuke your YMM).. What about end slashes etc..
                    if (
                        (this.link.startsWith('http') &&
                            !this.link.startsWith(`https://${this.$config.runtime.domain}`)) ||
                        this.openNewWindow
                    ) {
                        rootDisplayObj.is = 'a'
                        rootDisplayObj.attributes.href = this.link
                        rootDisplayObj.attributes.target = '_blank'
                        rootDisplayObj.attributes.rel = 'noopener'
                    } else if (this.link.includes('#')) {
                        rootDisplayObj.is = 'a'
                        const [path, id] = this.link.split('#')
                        if (this.link.startsWith('#') || this.$route.path.includes(path))
                            rootDisplayObj.events.click = () => {
                                this.$scrollTo(`#${id || path}`)
                            }
                        else rootDisplayObj.attributes.href = this.link
                    } else if (this.link.startsWith('tel') || this.link.startsWith('mailto')) {
                        rootDisplayObj.is = 'a'
                        rootDisplayObj.attributes.href = this.link
                    } else {
                        rootDisplayObj.is = 'nuxt-link'
                        let linkFormatted = this.link
                        if (linkFormatted.includes('?')) {
                            if (!linkFormatted.includes('/?'))
                                linkFormatted = linkFormatted.replace('?', '/?')
                        } else if (linkFormatted.slice(-1) !== '/') linkFormatted += '/'

                        rootDisplayObj.attributes.to = {
                            path: this.getRefittedUrl(
                                linkFormatted.replace(`https://${this.$config.runtime.domain}`, '')
                            ),
                        }

                        rootDisplayObj.attributes.event = this.$storyblok.isEditing ? '' : 'click'
                    }

                    break
            }

            // If we are editing, remove all click events from the event.
            // NOTE: we do this differently for nuxt-links, check out the nuxt-link case above.
            if (this.$storyblok.isEditing) rootDisplayObj.events = {}

            return rootDisplayObj
        },
        hasFullFitment() {
            return this.$store.getters['fitment/hasFullFitment']
        },
        fitmentData() {
            return this.$store.state.fitment.data
        },
    },
    methods: {
        hideSelector(selector) {
            this.$storyblok.hide(selector)
        },
        showSelector(selector) {
            this.$storyblok.show(selector)
        },
        openOrderLogin() {
            this.$overlay.show('orderlogin')
        },
        openAccountLogin() {
            if (!this.$cognito.enabled) return

            this.$cognito.openModal(() => this.$push.to({ name: 'account' }))
        },
        openChat() {
            if (!this.$chat.enabled) return
            this.$chat.open()
        },
        openFitment() {
            if (this.hasFullFitment) {
                this.$push.to({ name: 'mmy', params: this.fitmentData })
                return
            }

            this.$store.dispatch('fitment/showFitmentModal', { isMMYMode: true })
        },
        openISSD() {
            if (this.hasFullFitment) {
                this.$push.to({
                    name: 'mmy',
                    params: this.fitmentData,
                    query: { sameDayShipping: true },
                })
                return
            }
            this.$store.dispatch('fitment/showFitmentModal', { isSameDayShippingMode: true })
        },
        trackLinkEvent() {
            // if we don't have a link or are in preview mode we should not send the track event
            if (!this.link || this.$storyblok.isPreview) return

            const eventData = {
                clickType: 'Link',
                clickUrl: this.link,
                linkText: this.$el.innerText.replace('\n', ' '),
            }

            // Find image tag within link
            const [image] = this.$el.getElementsByTagName('img')

            // Find button tag within link
            const [button] = this.$el.getElementsByTagName('button')

            // If a button exists then change the click type
            if (button) {
                eventData.clickType = 'Button'
            }

            // If an image exists then get the src
            if (image) {
                eventData.clickType = 'Image'
                eventData.imageUrl = image.src
            }

            // The trackingEvent that gets passed should overwrite any found type
            if (this.trackingEventType) eventData.clickType = this.trackingEventType

            this.$uiEvents.$emit('cmsLinkClicked', eventData)
        },
    },
}
</script>
